import { Box, Grid, Heading, Card, Flex, Text } from 'theme-ui';
import { Company } from './types';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import { Map, GOOGLE_MAP_URL } from './CompanyInfo';
import { GrayFormikField } from '../../components/GrayFormikField';
import Button from '@/components/Button';
import {
    useGetCompanyCategoriesQuery,
    useGetCompanyTypesQuery,
    useGetCountriesQuery,
    usePutCompanyMutation,
} from '@/store/apiService';
import { CompanyFormValues, mapServerErrors, validateCompany } from './validateCompany';

interface Props {
    company: Company;
    onSaved?: () => void;
}

const toFormValues = (company: Company): CompanyFormValues => ({
    Name: company.Name ?? '',
    OrganisationNumber: company.OrganisationNumber ?? '',
    TypeId: company.TypeId != null ? String(company.TypeId) : '',
    CategoryId: company.CategoryId != null ? String(company.CategoryId) : '',
    Phone: company.Phone ?? '',
    Email: company.Email ?? '',
    SitePath: company.SitePath ?? '',
    Homepage: company.Homepage ?? '',
    Details: company.Details ?? '',
    Street1: company.Street1 ?? '',
    Street2: company.Street2 ?? '',
    ZipCode: company.ZipCode ?? '',
    City: company.City ?? '',
    CountryId: company.CountryId ?? '',
    Latitude: company.Latitude ?? '',
    Longitude: company.Longitude ?? '',
});

// GrayFormikField doesn't render errors for selects, so show them here
const SelectError = ({ error }: { error?: string }) =>
    error ? (
        <Text as="p" color="danger" mt="-1" mb="2">
            {error}
        </Text>
    ) : null;

export const CompanyInfoPartial = ({ company, onSaved }: Props) => {
    const [putCompany] = usePutCompanyMutation();
    const types = useGetCompanyTypesQuery();
    const categories = useGetCompanyCategoriesQuery();
    const countries = useGetCountriesQuery();

    const typeOptions = (types.data?.Results ?? []).map((t) => ({
        value: String(t.Id),
        label: t.Name ?? String(t.Id),
    }));
    const categoryOptions = (categories.data?.Results ?? []).map((c) => ({
        value: String(c.Id),
        label: c.Name ?? String(c.Id),
    }));
    const countryOptions = [
        { value: '', label: '—' },
        ...(countries.data?.Results ?? []).map((c) => ({
            value: c.Id as string,
            label: c.Name ?? (c.Id as string),
        })),
    ];

    return (
        <Formik
            enableReinitialize
            initialValues={toFormValues(company)}
            validate={validateCompany}
            onSubmit={(values, { setSubmitting, setErrors, resetForm }) => {
                putCompany({
                    CompanyId: company.Id as string,
                    Name: values.Name.trim(),
                    OrganisationNumber: values.OrganisationNumber.trim(),
                    TypeId: Number(values.TypeId),
                    CategoryId: Number(values.CategoryId),
                    Phone: values.Phone.trim(),
                    Email: values.Email.trim(),
                    SitePath: values.SitePath.trim(),
                    Homepage: values.Homepage.trim(),
                    Details: values.Details,
                    Street1: values.Street1,
                    Street2: values.Street2,
                    ZipCode: values.ZipCode,
                    City: values.City,
                    CountryId: values.CountryId || undefined,
                    Latitude: values.Latitude?.toString() ?? '',
                    Longitude: values.Longitude?.toString() ?? '',
                    // The API clears the logo when LogoType is omitted, so send the current one back
                    LogoType: company.LogoType,
                })
                    .unwrap()
                    .then(() => {
                        toast.success('Company information saved');
                        resetForm({ values });
                        onSaved?.();
                    })
                    .catch((err) => {
                        const fieldErrors = mapServerErrors(err);
                        if (Object.keys(fieldErrors).length > 0) {
                            setErrors(fieldErrors);
                        }
                        toast.error(
                            err?.data?.ResponseStatus?.Message ??
                                err?.data ??
                                err?.message ??
                                'Could not save company information',
                            { autoClose: 5000 }
                        );
                    })
                    .finally(() => setSubmitting(false));
            }}
        >
            {(props) => (
                <Box
                    as="form"
                    variant="segment"
                    sx={{ overflow: 'auto' }}
                    onSubmit={(e) => {
                        e.preventDefault();
                        props.handleSubmit();
                    }}
                >
                    <Flex sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <Heading>General</Heading>
                        <Flex sx={{ gap: 2 }}>
                            <Button
                                type="button"
                                variant="secondary"
                                disabled={!props.dirty || props.isSubmitting}
                                onClick={() => props.resetForm()}
                            >
                                Reset
                            </Button>
                            <Button
                                type="submit"
                                isLoading={props.isSubmitting}
                                disabled={!props.dirty || props.isSubmitting}
                            >
                                Save
                            </Button>
                        </Flex>
                    </Flex>
                    <Card variant="widget">
                        <Grid variant="column">
                            <Box variant="segment">
                                <GrayFormikField
                                    name="OrganisationNumber"
                                    type="text"
                                    label="Organisation number"
                                    required
                                />
                                <GrayFormikField name="Name" type="text" label="Name" required />
                                <GrayFormikField name="Phone" type="text" label="Phone" />
                                <GrayFormikField
                                    name="Email"
                                    type="email"
                                    label="Email"
                                    required
                                />
                                <GrayFormikField
                                    name="TypeId"
                                    type="select"
                                    label="Type"
                                    options={typeOptions}
                                    isLoading={types.isLoading}
                                    required
                                />
                                <SelectError error={props.errors.TypeId} />
                                <GrayFormikField
                                    name="CategoryId"
                                    type="select"
                                    label="Category"
                                    options={categoryOptions}
                                    isLoading={categories.isLoading}
                                    required
                                />
                                <SelectError error={props.errors.CategoryId} />
                                <GrayFormikField
                                    name="SitePath"
                                    type="text"
                                    label="Booking homepage"
                                    prefix="https://"
                                    required
                                />
                                <GrayFormikField
                                    name="Homepage"
                                    type="text"
                                    label="Homepage"
                                    placeholder="https://"
                                />
                                <GrayFormikField name="Details" type="textarea" label="Details" />
                            </Box>
                        </Grid>
                        <Grid variant="column">
                            <Heading>Address</Heading>
                            {process.env.NODE_ENV !== 'development' &&
                            company.Latitude &&
                            company.Longitude ? (
                                <Map
                                    // @ts-ignore
                                    googleMapURL={GOOGLE_MAP_URL}
                                    // @ts-ignore
                                    loadingElement={<div style={{ height: `100%` }} />}
                                    // @ts-ignore
                                    containerElement={<div style={{ height: `360px` }} />}
                                    // @ts-ignore
                                    mapElement={<div style={{ height: `100%` }} />}
                                    // @ts-ignore
                                    lat={parseFloat(company.Latitude)}
                                    // @ts-ignore
                                    lng={parseFloat(company.Longitude)}
                                />
                            ) : null}
                            <Box variant="segment">
                                <GrayFormikField name="Street1" type="text" label="Street 1" />
                                <GrayFormikField name="Street2" type="text" label="Street 2" />
                                <GrayFormikField name="ZipCode" type="text" label="Zip Code" />
                                <GrayFormikField name="City" type="text" label="City" />
                                <GrayFormikField
                                    name="CountryId"
                                    type="select"
                                    label="Country"
                                    options={countryOptions}
                                    isLoading={countries.isLoading}
                                />
                                <SelectError error={props.errors.CountryId} />
                                <Box variant="group">
                                    <GrayFormikField
                                        name="Latitude"
                                        type="text"
                                        label="Latitude"
                                    />
                                    <GrayFormikField
                                        name="Longitude"
                                        type="text"
                                        label="Longitude"
                                    />
                                </Box>
                            </Box>
                        </Grid>
                    </Card>
                    {Object.keys(props.errors).length > 0 && props.submitCount > 0 ? (
                        <Text color="danger">Please correct the highlighted fields</Text>
                    ) : null}
                </Box>
            )}
        </Formik>
    );
};
